import { supabase } from '../lib/supabase'
import { handleQuery, resolveRoleContext, accessDeniedResponse } from './utils'

const DEFAULT_FUNCTIONS_BASE_URL = '/.netlify/functions'

const getFunctionsBaseUrl = () =>
  import.meta.env.VITE_STRIPE_FUNCTIONS_BASE_URL || DEFAULT_FUNCTIONS_BASE_URL

const STRIPE_COLUMNS = 'stripe_connected_account_id, stripe_account_status, stripe_charges_enabled, stripe_payouts_enabled, stripe_onboarding_completed'

/**
 * Get Stripe Connect status for the current user (uses RLS; agent sees own, admin can pass userId).
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: { stripe_connected_account_id: string | null, stripe_account_status: string | null, stripe_charges_enabled: boolean, stripe_payouts_enabled: boolean, stripe_onboarding_completed: boolean } | null, error: object | null }>}
 */
export const getStripeConnectStatus = async (options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { role, userId } = roleContext
  if (role === 'student' || role === 'partner') {
    return accessDeniedResponse('Only agents can connect Stripe.')
  }

  if (!userId) {
    return { data: null, error: { message: 'User id is required.', code: 'access_denied' } }
  }

  const q = supabase
    .from('users')
    .select(STRIPE_COLUMNS)
    .eq('id', userId)
    .maybeSingle()

  return handleQuery(q, { fallbackMessage: 'Unable to load Stripe status.', allowEmpty: false })
}

/**
 * Request a Stripe Connect account link URL from the backend. Redirect the user to the returned URL.
 * @param {string} [accessToken] - Supabase session access_token (from getSession())
 * @returns {Promise<{ data: { url: string } | null, error: object | null }>}
 */
export const connectStripeAccount = async (accessToken) => {
  const baseUrl = getFunctionsBaseUrl()
  const url = `${baseUrl}/create-connect-account`
  const headers = { 'Content-Type': 'application/json' }
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    return {
      data: null,
      error: { message: data.error || response.statusText, code: 'connect_failed' },
    }
  }

  if (!data.url) {
    return { data: null, error: { message: 'No redirect URL returned.', code: 'connect_failed' } }
  }

  return { data: { url: data.url }, error: null }
}
